import { BadgeType } from 'components/badge/badge';

/** مطابق enumهای Domain.Entities.Enumerations.Marketing در بک‌اند. */
export enum MarketingActivityType {
    Call = 1,
    Sms = 2,
    Email = 3,
    Meeting = 4,
    Note = 5,
    Other = 6,
}

export enum MarketingActivityStatus {
    Todo = 1,
    Doing = 2,
    Done = 3,
    Canceled = 4,
    Failed = 5,
}

export enum MarketingActivitySource {
    Manual = 1,
    BulkSms = 2,
    Campaign = 3,
    CallTask = 4,
    GoldUpgrade = 5,
}

export enum MarketingCampaignType {
    UserReturn = 1,
}

export enum MarketingCampaignMemberReason {
    NotAuthenticated = 1,
    NotPurchased = 2,
}

export enum MarketingCampaignSmsStatus {
    Pending = 1,
    Sent = 2,
    Failed = 3,
}

export const activityTypeTitles: Record<MarketingActivityType, string> = {
    [MarketingActivityType.Call]: 'تماس',
    [MarketingActivityType.Sms]: 'پیامک',
    [MarketingActivityType.Email]: 'ایمیل',
    [MarketingActivityType.Meeting]: 'جلسه',
    [MarketingActivityType.Note]: 'یادداشت',
    [MarketingActivityType.Other]: 'سایر',
};

export const activityStatusBadges: Record<MarketingActivityStatus, { text: string; type: BadgeType }> = {
    [MarketingActivityStatus.Todo]: { text: 'در انتظار', type: 'info' },
    [MarketingActivityStatus.Doing]: { text: 'در حال انجام', type: 'waiting' },
    [MarketingActivityStatus.Done]: { text: 'انجام شده', type: 'success' },
    [MarketingActivityStatus.Canceled]: { text: 'لغو شده', type: 'secondary' },
    [MarketingActivityStatus.Failed]: { text: 'ناموفق', type: 'error' },
};

export const activitySourceTitles: Record<MarketingActivitySource, string> = {
    [MarketingActivitySource.Manual]: 'ثبت دستی',
    [MarketingActivitySource.BulkSms]: 'پیامک گروهی',
    [MarketingActivitySource.Campaign]: 'کمپین',
    [MarketingActivitySource.CallTask]: 'تسک تماس',
    [MarketingActivitySource.GoldUpgrade]: 'ارتقا به طلایی',
};

export const memberReasonTitles: Record<MarketingCampaignMemberReason, string> = {
    [MarketingCampaignMemberReason.NotAuthenticated]: 'احراز نکرده',
    [MarketingCampaignMemberReason.NotPurchased]: 'خرید نکرده',
};

export const campaignSmsBadges: Record<MarketingCampaignSmsStatus, { text: string; type: BadgeType }> = {
    [MarketingCampaignSmsStatus.Pending]: { text: 'در صف', type: 'waiting' },
    [MarketingCampaignSmsStatus.Sent]: { text: 'ارسال شده', type: 'success' },
    [MarketingCampaignSmsStatus.Failed]: { text: 'ناموفق', type: 'error' },
};

export interface MarketingActivity {
    id: number;
    userId: string;
    phoneNumber?: string;
    fullName?: string;
    type: MarketingActivityType;
    status: MarketingActivityStatus;
    source: MarketingActivitySource;
    description?: string;
    referenceId?: string;
    campaignId?: number;
    campaignTitle?: string;
    operatorName?: string;
    createdOn: string;
    doneOn?: string;
    lastModifiedDate?: string;
}

export interface CreateMarketingActivityBody {
    userId: string;
    type: MarketingActivityType;
    status: MarketingActivityStatus;
    description?: string;
}

export interface UpdateMarketingActivityBody {
    id: number;
    status: MarketingActivityStatus;
    description?: string;
}

export interface CallTask {
    userId: string;
    phoneNumber?: string;
    fullName?: string;
    registeredOn: string;
    /** LevelUniqueName بک‌اند: ۱ = پایه (احراز نکرده)، ۲ = نقره‌ای (احراز کرده و خرید نکرده) */
    level?: number;
    levelName?: string;
    campaignSmsStatus?: MarketingCampaignSmsStatus;
}

/** کاربری که مجموع خریدش از سقف گزارش بیشتر است و هنوز طلایی نیست. */
export interface GoldUpgradeCandidate {
    userId: string;
    fullName?: string;
    phoneNumber?: string;
    registeredOn: string;
    documentApprovedOn?: string;
    totalBuyAmount: number;
    buyCount: number;
    levelName?: string;
}

export interface GoldUpgradeCandidateDetails {
    userId: string;
    fullName?: string;
    phoneNumber?: string;
    nationalCode?: string;
    levelName?: string;
    images: { contentType?: string; createdOn: string; content: string }[];
}

export interface MarketingCampaign {
    id: number;
    type: MarketingCampaignType;
    title: string;
    description?: string;
    isActive: boolean;
    delayHours: number;
    notAuthenticatedMessage: string;
    notPurchasedMessage: string;
    senderLine: string;
    lastRunOn?: string;
    createdOn: string;
    membersCount: number;
    smsSentCount: number;
}

export interface UpdateMarketingCampaignBody {
    id: number;
    title: string;
    description?: string;
    isActive: boolean;
    delayHours: number;
    notAuthenticatedMessage: string;
    notPurchasedMessage: string;
}

export interface RunUserReturnCampaignResult {
    skipped: boolean;
    skipReason?: string;
    enrolledCount: number;
    notAuthenticatedCount: number;
    notPurchasedCount: number;
    sentCount: number;
    failedCount: number;
}

export interface CampaignReportDay {
    /** روز به وقت تهران (yyyy-MM-dd میلادی). */
    date: string;
    notAuthenticatedCount: number;
    notPurchasedCount: number;
    smsSentCount: number;
    authenticatedAfterCount: number;
    purchasedAfterCount: number;
}

export interface CampaignReport {
    campaignId: number;
    title: string;
    totalMembers: number;
    notAuthenticatedCount: number;
    notPurchasedCount: number;
    smsSentCount: number;
    smsFailedCount: number;
    smsPendingCount: number;
    authenticatedAfterCount: number;
    purchasedAfterCount: number;
    calledCount: number;
    days: CampaignReportDay[];
}

export interface CampaignMember {
    id: number;
    userId: string;
    phoneNumber?: string;
    fullName?: string;
    registeredOn: string;
    reason: MarketingCampaignMemberReason;
    enrolledOn: string;
    smsStatus: MarketingCampaignSmsStatus;
    isAuthenticated: boolean;
    purchasedAfter: boolean;
    called: boolean;
}
